import { useState } from 'react';
import { useT } from '../i18n/locale-context';
import type { TenderPosition } from '../lib/catalog-client';
import { withCurrency } from '../lib/format';
import { POSITIONS_PREVIEW, positionFigures, positionsCount } from '../lib/lot-positions';

/**
 * «Что закупают» / «Nima xarid qilinadi» — the lot's line items, above the
 * description on the tender page (live and closed lots alike).
 *
 * Why (audit 2026-10-17): a reader could not see WHAT a lot buys — the
 * description paraphrased whatever document we had extracted, for a tender-mc
 * lot a cost-structure sheet. The item list itself was already stored for four
 * sources and rendered only deep in «Условия поставки». It is public: names,
 * quantities and prices are shown to everyone, guests included (owner); the
 * API decides what money ships (`LOT_POSITION_PRICES_PUBLIC`).
 *
 * Mobile-first: every row is the name on its own line and the figures under
 * it, wrapping — no table, so nothing scrolls sideways at 390 px.
 * Renders nothing when the lot has no positions.
 */
export function LotPositionsCard({
  positions,
  total,
}: {
  positions: readonly TenderPosition[] | null | undefined;
  total?: number | null;
}) {
  const t = useT();
  const [expanded, setExpanded] = useState(false);
  const list = positions ?? [];
  if (list.length === 0) return null;

  const count = positionsCount(list, total);
  const currency = t('tender.budget.unit');
  const shown = expanded ? list : list.slice(0, POSITIONS_PREVIEW);
  const canExpand = list.length > POSITIONS_PREVIEW;

  return (
    <section
      id="sec-positions"
      data-testid="lot-positions"
      className="scroll-mt-16 rounded-lg border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6"
    >
      <h2 className="mb-2 text-[11px] font-bold uppercase tracking-[0.05em] text-ink-soft">
        {t('tender.positions.title')}
        {count > 1 && (
          <span className="ml-1.5 font-semibold tabular-nums text-ink-muted">{count}</span>
        )}
      </h2>
      <ol className="grid">
        {shown.map((p, i) => {
          const f = positionFigures(p);
          const parts: string[] = [];
          if (f.qty) parts.push(f.qty);
          if (f.unitPrice) {
            parts.push(
              t('tender.positions.unitPrice', { price: withCurrency(f.unitPrice, currency) }),
            );
          }
          if (f.total)
            parts.push(t('tender.positions.total', { sum: withCurrency(f.total, currency) }));
          return (
            <li
              key={i}
              className="border-t border-edge-light py-2.5 first:border-t-0 first:pt-0 last:pb-0"
            >
              <div className="break-words text-[14px] font-medium leading-snug text-ink">
                {p.name ?? `#${i + 1}`}
              </div>
              {parts.length > 0 && (
                <div className="mt-0.5 flex flex-wrap gap-x-2 text-[13px] tabular-nums text-ink-muted">
                  {parts.map((part, j) => (
                    <span key={j} className="whitespace-nowrap">
                      {j > 0 && (
                        <span aria-hidden="true" className="mr-2">
                          ·
                        </span>
                      )}
                      {part}
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ol>
      {canExpand && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-3 text-[13px] font-semibold text-brand transition-colors hover:text-brand-mid"
        >
          {expanded ? t('tender.positions.collapse') : t('tender.positions.showAll', { n: count })}
        </button>
      )}
      {expanded && count > list.length && (
        <p className="mt-2 text-[12px] text-ink-muted">
          {t('tender.positions.capped', { shown: list.length, n: count })}
        </p>
      )}
    </section>
  );
}
